import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import { produce } from 'immer';
import {
  catchError,
  combineLatest,
  concatMap,
  first,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
  timeout,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElement, CanvasElementId, getNewConnectionId } from '@blaze-runtime/engine-domain';
import { ApiActionInfo, CreateWorkflowRequest } from '@blaze-shared/api-contracts-domain';
import { generalSettingsSelectors } from '@blaze-shared/app-state-data-access';
import { ApisApiService } from '@blaze-shared/connectors-data-access';
import { ActionConfiguratorFormField } from '@blaze-shared/connectors-domain';
import { CanvasElementInputBinding, getActiveBinding } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetSchema, findColumn, WorkflowSchema } from '@blaze-shared/schema-domain';
import {
  Connection,
  findFirstNodeInWorkflowOfType,
  hasAssignments,
  isApiCallNode,
  isGetNode,
  isInsertNode,
  isNodeWithDatasetId,
  isNodeWithPrompt,
  isUpdateNode,
  Workflow,
  WorkflowNode,
  WorkflowNodeWithDialogId,
  WorkflowNodeWithPrompt,
  WorkflowSchemas,
} from '@blaze-shared/workflow-domain';

import { fromElements } from '../../+state';
import { ElementsFacade } from '../../+state/elements';
import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import {
  getDataSourceInputName,
  getDefaultOutputEventType,
} from '../../canvas-elements/element-model/io';
import { ActionConfiguratorService, ActionType } from '../../services/action-configurator.service';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { WorkflowCrudService } from '../../services/workflow-crud.service';
import { getNodeActionConfiguratorFormField } from '../../services/workflow-generation/public-api';
import { getUiRuntimeElement, getWorkflowRuntimeElement } from '../../utils/runtime';
import {
  AiWorkflowFinalizationEntry,
  ElementContext,
  type ResolvedLayoutInputCarrier,
  WorkflowContext,
} from '../models/ai.model';

import {
  AiWorkflowInputConnections,
  AiWorkflowInputVariable,
  RelatedDatasetMap,
  RelatedDetails,
  RelatedElementWorkflow,
} from './models';
import {
  mergeResolvedLayoutInputs,
  prepareRelatedDatasetMap,
  prepareRelatedWorkflows,
  processWorkflow,
} from './utils';

const AI_WORKFLOW_DIALOG_GENERATION_TIMEOUT_MS = 12_000;

@Injectable({ providedIn: 'root' })
export class AiWorkflowGenerationService {
  private readonly actionConfiguratorService = inject(ActionConfiguratorService);
  private readonly apisApiService = inject(ApisApiService);
  private readonly elementDefinitionService = inject(ElementDefinitionsService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  createWorkflow(
    context: ElementContext<any>,
    workflowContext: WorkflowContext,
  ): Observable<Workflow> {
    const request: CreateWorkflowRequest = {
      inputs: {},
      inputSchemas: [],
      isLongRunning: false,
      isServer: false,
      nodes: workflowContext.workflow.nodes,
      pageId: context.pageId,
      projectId: context.projectId,
      startNodeId: workflowContext.workflow.startNodeId,
    };

    return this.workflowCrudService.createWorkflow(request).pipe(
      switchMap((workflow) => {
        const datasetId = findFirstNodeInWorkflowOfType(workflow, isGetNode)?.datasetId;
        if (!datasetId) {
          return of(workflow);
        }

        return this.schemaService.get(datasetId).pipe(
          first(Boolean),
          map(() => workflow),
        );
      }),
    );
  }

  /**
   * Emits an array with related datasets and workflows and the reference to the element that's using them.
   */
  getWorkflowRelatedDetails(elementId: CanvasElementId): Observable<{
    relatedDatasetMap: RelatedDatasetMap;
    relatedWorkflows: RelatedElementWorkflow[];
    relatedDetails: RelatedDetails[];
  }> {
    return combineLatest({
      elements: this.elementsFacade.elements$,
      workflows: this.store.select(runtimeSelectors.selectWorkflows),
    })
      .pipe(
        // Search through elements in the same namespace
        switchMap(({ elements, workflows }) => {
          // Find target element's namespace
          const targetElement = elements.find(({ id }) => id === elementId);
          if (!targetElement) {
            throw new Error(`Element ${elementId} missing`);
          }
          const namespaceId = targetElement.ns;

          // Gather related elements' details
          const relatedDetails: Omit<RelatedDetails, 'columns'>[] = [];

          const elementsInNamespace = elements.filter((element) => element.ns === namespaceId);
          for (const element of elementsInNamespace) {
            if (targetElement.id === element.id) {
              continue;
            } // Ignore element for which the possible references are gathered
            else if (hasDataProps(element)) {
              const inputName = getDataSourceInputName(element.type);
              const binding = getActiveBinding(element.props.data, inputName);
              if (binding?.dataSource?.sourceType === 'dataset') {
                // If source is a dataset - add it to the related list
                relatedDetails.push({ datasetId: binding.dataSource.id, element: element });
              } else if (binding?.dataSource?.sourceType === 'context') {
                // If source is a workflow - look for a Get node with a dataset and add it to the related list
                const workflow = workflows.find(({ id }) => id === binding?.dataSource?.id);
                const getNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);
                const datasetId = getNode?.datasetId;

                relatedDetails.push({ datasetId, workflowId: workflow?.id, element });
              }
            }
          }

          // Return list of Observables emitting details columns obtained from schemas
          const details$: Observable<RelatedDetails>[] = relatedDetails.map((details) => {
            const idToObtain = details.workflowId || details.datasetId;

            const columns$ = idToObtain
              ? this.schemaService
                  .obtainSchema(idToObtain, !!details.workflowId)
                  .pipe(map((schema) => schema?.columns || []))
              : of([]);

            return columns$.pipe(map((columns) => ({ ...details, columns })));
          });

          return details$.length > 0 ? forkJoin(details$) : of([]);
        }),
      )
      .pipe(
        map((relatedDetails) => ({
          relatedDatasetMap: prepareRelatedDatasetMap(relatedDetails),
          relatedWorkflows: prepareRelatedWorkflows(relatedDetails),
          relatedDetails, // pass the unprocessed related details list further
        })),
      );
  }

  finalizeAiWorkflowCreation(
    elementId: CanvasElementId,
    workflow: Workflow,
    inputVariables: AiWorkflowInputVariable[],
    relatedDatasetMap: RelatedDatasetMap,
    relatedDetailsList: RelatedDetails[],
    bindingKey: CanvasElementInputBinding | undefined,
    forceWorkflowUpdate = false,
    visibleFields?: string[],
    resolvedLayoutInputs?: ResolvedLayoutInputCarrier,
  ): Observable<void> {
    return this.elementsFacade.entities$.pipe(
      first(),
      concatMap((entities) => {
        const element = entities[elementId];
        if (!element) {
          throw new Error(`Could not identify element ${elementId}`);
        }

        const { connections: connectionsMap, optionalInputs } = this.prepareConnections(
          inputVariables,
          relatedDetailsList,
          relatedDatasetMap,
          entities,
          element.ns,
          workflow.id,
        );

        // Prepare an updated workflow input dictionary
        const { inputs, connections } = mergeResolvedLayoutInputs(
          connectionsMap,
          resolvedLayoutInputs,
        );

        // Process the workflow
        const processedWorkflow = processWorkflow(workflow, inputs, optionalInputs);

        // If processing modified the workflow - update it on the server
        const workflowChanged = forceWorkflowUpdate || !isEqual(workflow, processedWorkflow);
        const updatedWorkflow$ = workflowChanged
          ? this.workflowCrudService.updateWorkflow(
              { ...processedWorkflow, id: workflow.id },
              connections,
            )
          : of(workflow);

        return combineLatest({
          updatedWorkflow: updatedWorkflow$,
          bindingKey: of(bindingKey ?? getDataSourceInputName(element.type)),
          inputConnections: of(connections),
          workflowChanged: of(workflowChanged),
        });
      }),
      first(),
      map(({ updatedWorkflow, bindingKey, inputConnections, workflowChanged }) => {
        this.applyWorkflowAsDataSource({
          elementId,
          bindingKey,
          workflow: updatedWorkflow,
          inputConnections,
          execute: !workflowChanged,
          visibleFields,
        });
      }),
    );
  }

  applyWorkflowAsDataSource({
    elementId,
    bindingKey,
    workflow,
    inputConnections,
    execute,
    visibleFields,
  }: {
    elementId: CanvasElementId;
    bindingKey: CanvasElementInputBinding;
    workflow: Workflow;
    inputConnections: Connection[];
    execute: boolean;
    /** Restricts the refreshed binding to these fields; omit to refresh every field. */
    visibleFields?: string[];
  }): void {
    const dataSourceName = this.schemaService.getTableName(
      findFirstNodeInWorkflowOfType(workflow, isGetNode)?.datasetId,
    );

    this.store.dispatch(
      runtimeActions.applyAiGeneratedWorkflow({
        elementId,
        bindingKey,
        dataSourceName,
        workflow,
        inputConnections,
      }),
    );
    this.store.dispatch(
      fromElements.refreshBinding({
        elementId,
        bindingKey,
        isNewDataSource: true,
        visibleFields,
      }),
    );

    if (execute) {
      this.store.dispatch(runtimeActions.executeWorkflow({ workflowId: workflow.id }));
    }
  }

  handleAiGeneratedWorkflow(workflow: Workflow): Observable<Workflow> {
    if (!workflow?.nodes.length) {
      return of(workflow);
    }

    return this.processAiWorkflowDialogs(workflow).pipe(
      switchMap((processed) =>
        processed === workflow ? of(workflow) : this.workflowCrudService.updateWorkflow(processed),
      ),
    );
  }

  /**
   * Processes AI-generated workflows so that any node with dialogAssignments but no dialogId
   * gets a generated dialog page (Show on Form). For every input entry:
   *  - Dialogs are created for each eligible node (the created dialog page is dispatched to the
   *    store as part of `createOrUpdatePopupForm`).
   *  - The workflow is persisted via the workflow CRUD service only when something actually
   *    changed: either a dialog was generated, or `forceUpdate` was set by the caller (e.g.
   *    `addEventWorkflowWithConnection` already mutated the workflow's inputs).
   *  - Persisted workflows are dispatched to the runtime store via `workflowsUpdate` so the
   *    canvas reflects the new dialogId without requiring a reload.
   */
  handleAiGeneratedWorkflows(entries: AiWorkflowFinalizationEntry[]): Observable<Workflow[]> {
    if (!entries.length) {
      return of([]);
    }

    return forkJoin(
      entries.map(({ workflow, forceUpdate }) =>
        this.processAiWorkflowDialogs(workflow).pipe(
          timeout(AI_WORKFLOW_DIALOG_GENERATION_TIMEOUT_MS),
          catchError((error) => {
            console.warn(`AI dialog generation failed for workflow ${workflow.id}`, error);
            return of(workflow);
          }),
          switchMap((processed) => {
            const dialogGenerated = processed !== workflow;
            if (!dialogGenerated && !forceUpdate) {
              return of({ workflow, persisted: false as const });
            }
            return this.workflowCrudService.updateWorkflow(processed).pipe(
              first(),
              map((updated) => ({ workflow: updated, persisted: true as const })),
              catchError((error) => {
                console.warn(`Saving AI generated workflow ${workflow.id} failed`, error);
                return of({ workflow, persisted: false as const });
              }),
            );
          }),
        ),
      ),
    ).pipe(
      tap((results) => {
        const persisted = results.filter((r) => r.persisted).map((r) => r.workflow);
        if (persisted.length) {
          this.store.dispatch(runtimeActions.workflowsUpdate({ workflows: persisted }));
        }
      }),
      map((results) => results.map((r) => r.workflow)),
    );
  }

  /**
   * Recursively creates dialogs for every node in the workflow that has dialogAssignments
   * but no dialogId yet. Returns the in-memory workflow with dialogId set on each processed
   * node. Does NOT persist the workflow — callers are responsible for that.
   *
   * `attemptedNodeIds` guards against infinite recursion when dialog creation is a no-op
   * (e.g. when `createOrUpdatePopupForm` returns no id).
   */
  private processAiWorkflowDialogs(
    workflow: Workflow,
    attemptedNodeIds: Set<string> = new Set(),
  ): Observable<Workflow> {
    const node = workflow.nodes.find(
      (n): n is WorkflowNodeWithPrompt => !attemptedNodeIds.has(n.id) && needsAiDialogGeneration(n),
    );

    if (!node) {
      return of(workflow);
    }

    attemptedNodeIds.add(node.id);

    return this.createDialogForAiNode(workflow, node).pipe(
      switchMap((updated) => this.processAiWorkflowDialogs(updated, attemptedNodeIds)),
    );
  }

  /**
   * Creates a dialog for a single AI-generated workflow node and returns the workflow
   * with the new dialogId set on that node. If `createOrUpdatePopupForm` resolves without a
   * dialogId (e.g. no fields are marked Show on Form) the original workflow is returned
   * unchanged.
   */
  private createDialogForAiNode(
    workflow: Workflow,
    node: WorkflowNodeWithPrompt,
  ): Observable<Workflow> {
    const actionType = this.getAiNodeActionType(node);

    const dialogAssignments = node.dialogAssignments;
    const assignments = hasAssignments(node) ? node.assignments : undefined;
    if (!assignments?.length || !dialogAssignments?.length) {
      return of(workflow);
    }

    const datasetSchema$ = isNodeWithDatasetId(node)
      ? this.schemaService.obtainSchema<DatasetSchema>(node.datasetId)
      : of(undefined);
    const apiCallInfo$ = isApiCallNode(node)
      ? this.getApiCallNodeInfo(node.connectorId, node.actionName)
      : of(undefined);

    const schemas$: Observable<WorkflowSchemas | undefined> = of({} as any);
    const input1RowSchema$ = this.getInput1RowSchema$(node, datasetSchema$, schemas$);

    return this.actionConfiguratorService
      .getSchemaFromActionType(actionType, apiCallInfo$, datasetSchema$, input1RowSchema$)
      .pipe(
        first(),
        concatLatestFrom(() =>
          this.store.select(generalSettingsSelectors.selectEditOrViewDetailsAs),
        ),
        switchMap(([schema, actionPageType]) => {
          const showOnFormMap: Record<string, boolean> = Object.fromEntries(
            dialogAssignments.map(({ name }) => [name, true]),
          );
          const assignmentsFields = assignments
            .map((a) => {
              const column = findColumn(schema?.columns, a.name);
              if (!column) {
                return undefined;
              }

              return getNodeActionConfiguratorFormField({
                column,
                nodeAssignments: assignments,
                nodeDialogAssignments: dialogAssignments,
                showOnFormMap,
              }).field;
            })
            .filter(Boolean) as ActionConfiguratorFormField[];

          const currentActionConfig =
            this.actionConfiguratorService.getActionTypeConfig(actionType);
          const popupData = this.actionConfiguratorService.preparePopupFormGenerationData(
            workflow.id,
            currentActionConfig,
            assignmentsFields,
            schema,
            actionPageType,
          );
          return this.actionConfiguratorService.createOrUpdatePopupForm(popupData).pipe(
            map((dialogId) => {
              if (!dialogId) {
                return workflow;
              }
              return produce(workflow, (draft) => {
                const promptNode = draft.nodes.find(
                  (n) => n.id === node.id,
                ) as WorkflowNodeWithDialogId;
                promptNode.dialogId = dialogId;
              });
            }),
          );
        }),
      );
  }

  private getAiNodeActionType(node: WorkflowNodeWithPrompt): ActionType | undefined {
    if (isInsertNode(node)) {
      return 'insert';
    }
    if (isUpdateNode(node)) {
      return 'update-row';
    }
    return isApiCallNode(node) ? 'api-call' : undefined;
  }

  private getInput1RowSchema$(
    node: WorkflowNodeWithPrompt,
    datasetSchema$: Observable<DatasetSchema | undefined>,
    schemas$: Observable<WorkflowSchemas | undefined>,
  ): Observable<DatasetSchema | WorkflowSchema | undefined> | undefined {
    if (!isUpdateNode(node)) {
      return undefined;
    }
    if (node.currentRow === false) {
      return datasetSchema$;
    }
    return schemas$.pipe(
      switchMap((schemas) => {
        const inputSchema = schemas?.nodeInputSchemasMap?.get(node.id);
        return inputSchema?.tableId ? of(inputSchema) : datasetSchema$;
      }),
    );
  }

  private getApiCallNodeInfo(
    connectorId: string | undefined,
    actionName: string | undefined,
  ): Observable<ApiActionInfo | undefined> {
    if (!connectorId || !actionName) {
      return of(undefined);
    }
    return this.apisApiService
      .getApiActionInfo(connectorId, actionName)
      .pipe(catchError(() => of(undefined)));
  }

  private prepareConnections(
    inputVariables: AiWorkflowInputVariable[],
    relatedDetailsList: RelatedDetails[],
    relatedDatasetMap: RelatedDatasetMap,
    entities: Record<string, CanvasElement | undefined>,
    namespaceId: string,
    workflowId: string,
  ): AiWorkflowInputConnections {
    const connectionMap = new Map<string, Connection>();
    const optionalInputs = new Set<string>(); // Gather the input names which should be marked as optional in conditions which use them

    for (const { name, datasetId, workflowId: inputWorkflowId } of inputVariables) {
      const relatedElementId = inputWorkflowId
        ? relatedDetailsList.find((related) => related.workflowId === inputWorkflowId)?.element.id
        : datasetId && relatedDatasetMap.get(datasetId);

      const relatedElement = relatedElementId ? entities[relatedElementId] : undefined;
      if (!relatedElementId || !relatedElement) {
        console.warn(
          `Could not find related element (${relatedElementId}) for dataset (${datasetId})`,
        );
        continue;
      }

      const eventType = getDefaultOutputEventType(
        this.elementDefinitionService.getIoConfig(relatedElement.type),
      );
      if (!eventType) {
        throw new Error(`Could not identify event type for related element (${relatedElement.id})`);
      }

      // Remember if input coming from an element should be marked as optional in conditions using its data
      const isOptionalInput = this.elementDefinitionService.getFeature(
        relatedElement.type,
        (feature) => feature.ai?.optionalInput,
      );
      if (isOptionalInput) {
        optionalInputs.add(name);
      }

      connectionMap.set(name, {
        id: getNewConnectionId(namespaceId),
        source: getUiRuntimeElement(relatedElementId),
        target: getWorkflowRuntimeElement(workflowId),
        eventType,
      });
    }

    return {
      connections: Object.fromEntries(connectionMap),
      optionalInputs: Array.from(optionalInputs),
    };
  }
}

function needsAiDialogGeneration(node: WorkflowNode): node is WorkflowNodeWithPrompt {
  if (!isNodeWithPrompt(node) || !hasAssignments(node) || node.dialogId) {
    return false;
  }
  const dialogAssignmentCount = node.dialogAssignments?.length ?? 0;
  const assignmentCount = node.assignments?.length ?? 0;
  return dialogAssignmentCount > 0 && assignmentCount > 0;
}
